import { Component, DestroyRef, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { NextFormComponent } from '@app/next/declarative-forms/components/declarative-form/next-form.component';
import {
  FormModalData,
  ModalComponent,
} from '@app/next/messaging/components/modal/modal.component';
import { FormActionEvent } from '@app/verify/feature/forms/components/form-page/form-page.component';
import { FormModal } from '@fmnts/api/next';
import { logger } from '@fmnts/common/log';
import { Modal, ModalConfig } from '@fmnts/components/modal';
import { isUndefined } from '@fmnts/core';
import {
  DonorDeviceSignalStore,
  SelfCheckoutStreamStore,
} from '@formunauts/self-checkout/data-access';
import { SelfCheckout } from '@formunauts/self-checkout/domain';
import { prepareFormData } from '@formunauts/shared/campaigning/data-access';
import { CampaignForm } from '@formunauts/shared/campaigning/domain';
import { LegacyFmntsApiError } from '@formunauts/shared/errors/domain';
import * as Sentry from '@sentry/angular';
import * as Arr from 'effect/Array';
import { pipe } from 'effect/Function';
import * as O from 'effect/Option';
import { EMPTY, catchError, filter, map } from 'rxjs';
import {
  ClientOfflineModalComponent,
  ClientTimeoutModalComponent,
  CookiesDisabledModalComponent,
} from '../components/form-modals/self-checkout-modals.component';
import * as Utils from '../self-checkout-feature-utils';

enum VerifyUnsubscribeAction {
  Unsubscribe = 'unsubscribe',
}

@Component({
  templateUrl: './self-checkout-unsubscribe.component.html',
})
export class SelfCheckoutUnsubscribeComponent {
  private readonly _log = logger({
    name: SelfCheckoutUnsubscribeComponent.name,
  });
  private readonly route = inject(ActivatedRoute);
  private readonly modal = inject(Modal);
  private readonly selfCheckoutStore = inject(SelfCheckoutStreamStore);
  protected readonly destroyRef = inject(DestroyRef);
  /** Holds information on the empty state */
  protected readonly emptyState = computed(() =>
    pipe(
      this.selfCheckoutStore.unsubscribeFormError(),
      O.map(Utils.getEmptyStateForGetForm),
      O.getOrNull,
    ),
  );
  protected hash = signal<SelfCheckout.Hash>(this.selfCheckoutStore.hash());
  protected unsubscribeForm = signal<SelfCheckout.UnsubscribeForm>(null!);
  protected readonly donorDeviceStore = inject(DonorDeviceSignalStore);
  private readonly _modals = signal<readonly FormModal[]>([]);

  constructor() {
    this.selfCheckoutStore.getUnsubscribeForm(this.hash());
    toObservable(this.selfCheckoutStore.unsubscribeForm)
      .pipe(filter(O.isSome), takeUntilDestroyed())
      .subscribe((unsubscribeForm) => {
        this.unsubscribeForm.set(unsubscribeForm.value);
        this.donorDeviceStore.setLanguages(unsubscribeForm.value.language);
        this.donorDeviceStore.setLocale(unsubscribeForm.value.locale);
      });

    toObservable(this.selfCheckoutStore.unsubscribedResponse)
      .pipe(filter(O.isSome), takeUntilDestroyed())
      .subscribe(() => {
        this.showSuccessModal(this._modals());
      });
    toObservable(this.selfCheckoutStore.unsubscribeResponseError)
      .pipe(filter(O.isSome), takeUntilDestroyed())
      .subscribe((error) => {
        this.showErrorModal(this._modals(), error.value);
      });
  }

  handleAction(event: FormActionEvent) {
    const { action, formComponent } = event;
    switch (action) {
      case VerifyUnsubscribeAction.Unsubscribe:
        this._modals.set(formComponent.page.modals);
        return this.submitForm(formComponent);
      default:
        this._log.error(`Unknown action: ${action}`);
    }
  }

  private submitForm(formComponent: NextFormComponent) {
    formComponent
      .submit$()
      .pipe(
        catchError(() => {
          this._log.info('the form is not valid');
          return EMPTY;
        }),
        map((formData) =>
          prepareFormData(
            formData,
            Arr.flatMap(formComponent.pages, CampaignForm.getFieldsOnPage),
          ),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((preparedFormData) => {
        this.selfCheckoutStore.unsubscribe({
          hash: this.hash(),
          formData: SelfCheckout.UnsubscribeCheckoutData(preparedFormData),
        });
      });
  }

  private showSuccessModal(modals: readonly FormModal[]) {
    const modalKey = 'unsubscriptionSuccessModal';
    // For now, there shouldn't be any buttons for the success modal
    this.openModalByKey(modalKey, modals, { disableClose: true });
  }

  private showErrorModal(
    modals: readonly FormModal[],
    error: SelfCheckout.SelfCheckoutError,
  ) {
    switch (error._tag) {
      case 'ClientTimeoutError':
        return this.modal.open(ClientTimeoutModalComponent);
      case 'ClientOfflineError':
        return this.modal.open(ClientOfflineModalComponent);
      case 'SelfCheckoutCSRFError':
        // Check for message containing CSRf verification, and show
        // custom error modal. This can happen e.g. when cookies are disabled
        // on the end device

        Sentry.captureException(
          new LegacyFmntsApiError({
            httpError: {
              ...error.httpError,
              message: error.httpError.error.message,
            },
          }),
        );
        return this.modal.open(CookiesDisabledModalComponent);
      default: {
        const modal = Utils.findHttpModalOrGenericFallback(
          modals,
          error instanceof SelfCheckout.SelfCheckoutCustomError
            ? (error.status ?? null)
            : null,
        );

        return this.modal.open(ModalComponent, {
          minWidth: '25vw',
          data: {
            modal: { ...modal },
          },
        });
      }
    }
  }

  /**
   * opens a modal based on the specified key
   * @param key - key used to identify the modal
   * @param modals - custom modals
   * @param config - optional configuration that can be passed to override default configuration
   * @returns reference to the opened modal
   */
  private openModalByKey(
    key: string,
    modals: readonly FormModal[],
    config: Readonly<ModalConfig> = {},
  ) {
    const modal = modals.find((currentModal) => currentModal.key === key);

    if (isUndefined(modal)) {
      throw new Error(
        `Modal with key ${key} can't be opened, because it can't be found.`,
      );
    }

    const modalData: FormModalData = {
      modal: { ...modal, ...config.data },
    };

    this.modal.open<ModalComponent, FormModalData, string>(ModalComponent, {
      minWidth: '25vw',
      ...config,
      data: modalData,
    });
  }
}
